import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { GridExpandMoreIcon } from '@mui/x-data-grid';
import { useMutation } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import dayjs from 'dayjs';
import { SyntheticEvent, useEffect, useLayoutEffect, useState } from 'react';
import { useLocation } from 'react-router';

import { EmptyContent, FullPageSpinner, Markdown, ReturnType, Spinner } from '@zeta-core';
import { QueryAPIs, TranscriptAPIs } from '@zeta-services';
import {
  IAllTranscriptsData,
  IClientCardItem,
  IFactFindEntity,
  ISingleTransciptDetailsRequest,
  ISingleTransciptDetailsResponse,
  IViewByIDTranscriptRequest,
  IViewByIDTranscriptResponse,
  IViewFactFindRequest,
} from '@zeta-types';

const AllTranscripts = ({ isAllTranscripts }: { isAllTranscripts: ReturnType }) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState<boolean | string>(false);
  const location = useLocation();
  const [viewAllTranscriptData, setViewAllTranscriptData] = useState<IAllTranscriptsData>();
  const [viewSingleAllTranscriptData, setViewSingleAllTranscriptData] = useState<ISingleTransciptDetailsResponse>();

  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [transcript, setTranscript] = useState<ISingleTransciptDetailsResponse | null>(null);
  const [isAudio, setIsAudio] = useState(false);
  const [startTime, setStartTime] = useState('00:00:00');

  useLayoutEffect(() => {
    (document.body.style as any)['overflow-x'] = 'hidden';
    return () => {
      (document.body.style as any)['overflow-x'] = 'auto';
    };
  }, []);

  const viewTranscriptByID = useMutation({
    mutationFn: (payload: IViewByIDTranscriptRequest) => TranscriptAPIs().viewTranscriptByID(payload),

    onSuccess: (response: AxiosResponse<IViewByIDTranscriptResponse>) => {
      const { data: responseData } = response;
      if (responseData?.audioFile) {
        const formData = new FormData();
        if (transcript?.transcriptID !== undefined) {
          formData.append('transcriptID', transcript?.transcriptID);
        }
        streamTranscript.mutateAsync(formData);
        setIsAudio(true);
        setStartTime('00:00:00');
      }
    },
  });

  const streamTranscript = useMutation({
    mutationFn: async (payload: FormData) => {
      const resp = await TranscriptAPIs().streamTranscript(payload);
      const url = URL.createObjectURL(resp?.data);
      setAudioUrl(url);
    },
  });

  const state = (location.state || {}) as { entityData: IFactFindEntity; clientData: IClientCardItem };

  const viewAllTranscriptsAPI = useMutation({
    mutationFn: (payload: IViewFactFindRequest) => QueryAPIs().viewAllTranscript(payload),

    onSuccess: (data: AxiosResponse<IAllTranscriptsData>) => {
      setViewAllTranscriptData(data.data);
    },
  });

  const viewSingleAllTranscriptAPI = useMutation({
    mutationFn: (payload: ISingleTransciptDetailsRequest) => QueryAPIs().viewSingleAllTranscript(payload),

    onSuccess: (data: AxiosResponse<ISingleTransciptDetailsResponse>) => {
      setViewSingleAllTranscriptData(data.data);

      const id = data.data?.transcriptID;
      if (data.data?.transcriptID) {
        setTranscript(data.data);
      }

      if (id) {
        viewTranscriptByID.mutateAsync({ transcriptId: id });
      }
    },
  });

  useEffect(() => {
    viewAllTranscriptsAPI.mutateAsync({ caseID: state?.clientData?.caseID });
  }, []);

  const handleAccordionChange = (panel: boolean | string, transcriptID: string) => (event: SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);

    if (isExpanded) {
      viewSingleAllTranscriptAPI.mutate({ transcriptID });
    } else {
      setAudioUrl(null);
      setIsAudio(false);
    }
  };

  const descriptionData = [
    viewSingleAllTranscriptData?.bankAccountDescription,
    viewSingleAllTranscriptData?.businessAssetDescription,
    viewSingleAllTranscriptData?.investmentAccountDescription,
    viewSingleAllTranscriptData?.otherAssetsDescription,
    viewSingleAllTranscriptData?.pensionAccountDescription,
    viewSingleAllTranscriptData?.realEstateDescription,
  ].filter((item) => item);

  return (
    <Stack sx={{ zIndex: 3 }} gap={2}>
      <Stack flexDirection="row" justifyContent="space-between">
        <Stack>
          <Typography variant="h6">All Transcripts</Typography>
          <Typography variant="caption" color="text.secondary">
            Detailed overview of all fact find meeting transcripts, summarising key discussions at each phase of the financial planning process.
          </Typography>
        </Stack>
      </Stack>
      {viewAllTranscriptsAPI?.isPending ? (
        <FullPageSpinner />
      ) : (
        <>
          <Stack pb={5 * 5}>
            {viewAllTranscriptData?.transcripts?.length === 0 ? (
              <Stack marginTop={4}>
                <EmptyContent title="No Conversation" />
              </Stack>
            ) : (
              <>
                {viewAllTranscriptData?.transcripts.map((meeting, index) => (
                  <Accordion
                    key={meeting.transcriptID}
                    expanded={expanded === meeting.transcriptID}
                    onChange={handleAccordionChange(meeting.transcriptID, meeting.transcriptID)}
                    sx={{
                      mb: 1,
                      borderRadius: 2,
                      boxShadow: '0px 0px 2px 0px rgba(145, 158, 171, 0.20), 0px 12px 24px -4px rgba(145, 158, 171, 0.12)',
                      border: 'none',
                      '&:not(.Mui-expanded):first-of-type': {
                        borderTopLeftRadius: 16,
                        borderTopRightRadius: 16,
                      },
                      '&::before': {
                        display: 'none',
                      },
                    }}
                  >
                    <AccordionSummary expandIcon={<GridExpandMoreIcon />} aria-controls={`panel${index}-content`} id={`panel${index}-header`}>
                      <Stack>
                        <Stack flexDirection="row" marginBottom={2}>
                          <Chip label={dayjs(meeting.uploadTime).format('DD MMM YYYY, hh:mm A')} size="small" variant="soft" color="info" />
                        </Stack>
                        <Stack>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {meeting.transcriptFileName}
                          </Typography>
                        </Stack>
                        <Typography variant="body2" color="textSecondary">
                          {meeting.detail}
                        </Typography>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      {!viewSingleAllTranscriptAPI?.isPending ? (
                        <Card sx={{ border: `1px solid ${theme.palette.primary.main}` }}>
                          <CardContent sx={{ padding: 1.5 }}>
                            <Stack gap={1.5}>
                              <Stack gap={2}>
                                {viewSingleAllTranscriptData?.clientCurrentSituation?.[0] ? (
                                  <>
                                    <Markdown children={viewSingleAllTranscriptData?.clientCurrentSituation?.[0] ?? ''} />
                                    {viewSingleAllTranscriptData?.businessInterest?.[0] && (
                                      <Markdown children={viewSingleAllTranscriptData?.businessInterest[0] ?? ''} />
                                    )}
                                    {viewSingleAllTranscriptData?.trust?.[0] && <Markdown children={viewSingleAllTranscriptData?.trust[0] ?? ''} />}
                                  </>
                                ) : (
                                  <EmptyContent title="No Data Available" />
                                )}
                              </Stack>
                            </Stack>
                            <Stack mt={1.5}>
                              {viewSingleAllTranscriptData?.conversations?.length > 0 && (
                                <Stack marginTop={1.25}>
                                  {viewSingleAllTranscriptData?.conversations?.map((conversation, indexConversation) => {
                                    return (
                                      <Stack key={conversation.conversationID} marginTop={1.25} marginLeft={1}>
                                        {conversation.conversationText?.map((conversationText) => {
                                          return (
                                            <Typography
                                              key={conversationText.textSpeaker}
                                              variant="subtitle2"
                                              sx={{ bgcolor: indexConversation % 2 === 0 ? alpha(theme.palette.primary.light, 0.3) : 'transparent' }}
                                            >
                                              {conversationText.textSpeaker}: {conversationText.text}
                                            </Typography>
                                          );
                                        })}
                                      </Stack>
                                    );
                                  })}
                                </Stack>
                              )}
                            </Stack>
                          </CardContent>
                        </Card>
                      ) : (
                        <Stack width="100%" height={200} justifyContent="center" alignItems="center">
                          <Spinner />
                        </Stack>
                      )}
                    </AccordionDetails>
                  </Accordion>
                ))}
              </>
            )}
          </Stack>
          {isAudio && (
            <Box sx={{ position: 'absolute', bottom: 5, width: '80%', overflowX: 'hidden' }}>
              <Container sx={{ width: 'fill-available', ml: 0, px: '0 !important' }} maxWidth="xl">
                {audioUrl ? (
                  <audio controls style={{ width: '100%' }}>
                    <source src={audioUrl + `#t=${startTime}`} type="audio/mp4" />
                    Your browser does not support the audio element.
                  </audio>
                ) : (
                  <Card variant="outlined">
                    <Stack direction="row" alignItems="center" sx={{ width: '100%' }}>
                      <Box p={1.5}>
                        <Spinner />
                      </Box>
                      <Divider orientation="vertical" flexItem />
                      <Stack direction="row" justifyContent="space-between" sx={{ width: '100%' }}>
                        <Typography variant="body2" ml={1.5} sx={{ flex: 1 }}>
                          The audio is loading up! Take a moment to review the summary while you wait.{' '}
                        </Typography>
                        <Typography variant="subtitle2" pr={1.5}>
                          0:00:00
                        </Typography>
                      </Stack>
                    </Stack>
                  </Card>
                )}
              </Container>
            </Box>
          )}
        </>
      )}
      <Stack sx={{ position: 'fixed', bottom: 0, right: 0, p: 3 }} onClick={isAllTranscripts.onFalse}>
        <Button variant="outlined">Close</Button>
      </Stack>
    </Stack>
  );
};

export default AllTranscripts;
